import React, { useState, useCallback } from 'react';
import classnames from "classnames";
import User from '~/model/user';
import { useSocketOn, useSubscribeToOrderSigner, useSubscribeToUser } from '~/hooks/socket';
import socketEvent from '~/constants/socketEvent';
import { formats, formatTimestamp } from '~/helpers/dateHelper';
import { randomStringSync } from '~/helpers/utilHelper';
import { UncontrolledTooltip } from "reactstrap";

type TUserActivityStatusProps = {
  id?: number;
  status?: number;
  lastSeen?: number;
  hideText?: boolean;
  size: string;
  autoUpdate?: boolean;
  skipSubscription?: boolean;
  className?: string;
  isSigner?: boolean;
  showTooltip?: boolean;
}

const UserActivityStatus = ({ id, status, lastSeen, hideText, size, autoUpdate, skipSubscription, className, isSigner, showTooltip }: TUserActivityStatusProps) => {

  /********** STATE **********/

  const [activityInfo, setActivityInfo] = useState({ status, lastSeen });

  /********** SOCKET **********/

  const shouldSubscribe = useCallback((): boolean => !!(autoUpdate && !skipSubscription && id), [autoUpdate, skipSubscription, id]);
  const shouldSubscribeToUser = useCallback((): boolean => !isSigner && shouldSubscribe(), [isSigner, shouldSubscribe]);
  const shouldSubscribeToOrderSigner = useCallback((): boolean => !!(isSigner && shouldSubscribe()), [isSigner, shouldSubscribe]);

  // start receiving updates about this particular user
  // but only if auto-update has been requested
  // and if we should not skip the subscription part
  // and if an id has been provided
  useSubscribeToUser(id, shouldSubscribeToUser);
  useSubscribeToOrderSigner(id, shouldSubscribeToOrderSigner);

  // since this function does not depend on external variables
  // we can cache it between renders for performance reasons
  // also to prevent adding the event to the socket on every render
  const onUserActivityStatusChanged = useCallback((data: { id: number; status: number; lastActive: number }) => {
    // this socket client is shared by the entire app
    // and here we are listening for an event that might be triggered by multiple users
    // therefore we need to check whether this update refers to the current user
    if (data.id == id) {
      setActivityInfo({
        status: data.status,
        lastSeen: data.lastActive,
      });
    }
  }, [id]);

  const eventName = isSigner ? socketEvent.orderSignerActivityStatusChanged : socketEvent.userActivityStatusChanged;
  const shouldListen = useCallback((): boolean => !!(autoUpdate && id), [autoUpdate, id]);

  // listen for changes on user activity status
  // but only if auto-update has been requested and an id has been provided
  useSocketOn(eventName, onUserActivityStatusChanged, shouldListen);

  /********** OTHER **********/

  const getStatusColor = () => {
    switch (activityInfo.status) {
      case User.ACTIVITY_STATUS_ACTIVE:
        return 'bg-success';
      case User.ACTIVITY_STATUS_IDLE:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  }

  const uid = randomStringSync(2);

  const shouldShowTimeSince = () => !!activityInfo.lastSeen && activityInfo.status != User.ACTIVITY_STATUS_ACTIVE;

  return <React.Fragment>
    {activityInfo.status !== undefined && <div className={classnames('user-activity-status', `user-activity-status-${activityInfo.status}`, `user-activity-status-${size}`, className)} id={'usractstat' + uid}>
      <i className={classnames('user-activity-status-circle', getStatusColor())}></i>
      {!hideText && <span className="user-activity-status-text ms-1">
        <span className="user-activity-status-name">{User.getActivityStatusName(activityInfo.status)}</span>
        {shouldShowTimeSince() && activityInfo.lastSeen !== undefined && <span className="user-activity-status-last-seen ms-2 badge bg-light text-secondary align-middle">since {formatTimestamp(activityInfo.lastSeen, formats.SHORT_US_DATETIME)}</span>}
      </span>}
      {showTooltip && shouldShowTimeSince() && activityInfo.lastSeen !== undefined && <UncontrolledTooltip placement="top" target={'usractstat' + uid}>since {formatTimestamp(activityInfo.lastSeen, formats.SHORT_US_DATETIME)}</UncontrolledTooltip>}
    </div>}
  </React.Fragment>
}

export default UserActivityStatus;